// Building blocks for the Service Desk / Stock / Procurement / Dashboard pages.
// They follow the existing App.js look (page-heading, review-modal, table-section...)
// and add the orange/white styles from styles/theme.css.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FileText, Inbox, Paperclip, X } from "lucide-react";
import { API, call } from "@/lib/api";

export const FeaturesContext = createContext({});
export const useFeatures = () => useContext(FeaturesContext);

// ---------------------------------------------------------------- formatting
export const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const timeAgo = (v) => {
  if (!v) return "—";
  const s = Math.max(0, (Date.now() - new Date(v).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(v);
};
export const fmtDayTime = (v) => (v ? new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
// Tickets raised in the last 24 hours get a NEW badge in the queues.
export const isRecent = (v, hours = 24) => Boolean(v) && Date.now() - new Date(v).getTime() < hours * 3600 * 1000;
// "4d ago" with the real date underneath, so a screenshot still shows when it happened.
export const AgeCell = ({ value }) => <span className="fx-age"><span>{timeAgo(value)}</span>{value && <small className="fx-mono fx-muted">{fmtDayTime(value)}</small>}</span>;
export const NewBadge = () => <span className="fx-new" data-testid="new-badge">NEW</span>;
// Value that follows `value` once it has stopped changing for `ms` (search-as-you-type).
export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}
export const money = (v, currency = "INR") => (v === null || v === undefined || v === "" ? "—" : new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(v)));
const SPECIAL = { it_admin: "IT Admin", super_admin: "Super Admin", hr: "HR", employee: "Employee" };
export const humanize = (v) => (v ? SPECIAL[v] || String(v).replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase()) : "—");

// ---------------------------------------------------------------- statuses
const STATUS = {
  open: ["Open", "blue"], in_progress: ["In progress", "orange"], awaiting_employee: ["Waiting on you", "amber"], on_hold: ["On hold", "gray"],
  awaiting_approval: ["Awaiting approval", "purple"], procurement: ["Procurement", "purple"], resolved: ["Resolved", "green"], closed: ["Closed", "gray"],
  pending_approval: ["Pending approval", "purple"], approved: ["Approved", "green"], rejected: ["Rejected", "red"], implemented: ["Implemented", "green"], cancelled: ["Cancelled", "gray"],
  ordered: ["Ordered", "blue"], partially_received: ["Partly received", "amber"], received: ["Received", "green"],
  active: ["Allocated", "green"], in_stock: ["In stock", "blue"], returned: ["In stock", "blue"], damaged: ["Damaged", "red"], in_repair: ["In repair", "amber"], retired: ["Retired", "gray"], disposed: ["Disposed", "gray"],
  pending: ["Pending", "amber"], verified: ["Verified", "green"], partially_returned: ["Partly returned", "amber"],
};
const ADMIN_LABEL = { awaiting_employee: "Awaiting employee" };
export const statusText = (s, admin) => (admin && ADMIN_LABEL[s]) || (STATUS[s] || [humanize(s)])[0];
export function StatusPill({ status, admin }) {
  const [, tone] = STATUS[status] || [null, "gray"];
  return <span className={`fx-pill ${tone}`} data-testid={`pill-${status}`}>{statusText(status, admin)}</span>;
}
const PRIORITY = { critical: "red", high: "orange", medium: "blue", low: "gray" };
export const PriorityPill = ({ priority }) => <span className={`fx-pill ${PRIORITY[priority] || "gray"}`}>{humanize(priority)}</span>;

// ---------------------------------------------------------------- layout
export function PageHeading({ eyebrow, title, copy, action }) {
  return <div className="page-heading"><div><p className="eyebrow">{eyebrow}</p><h1 data-testid="page-title">{title}</h1>{copy && <p className="page-copy">{copy}</p>}</div>{action}</div>;
}
export function Stat({ label, value, hint, icon: Icon, tone, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return <Tag className={`fx-stat ${tone || ""}`} onClick={onClick} data-testid={testId}><span className="label">{Icon && <Icon size={14} />}{label}</span><span className="value">{value ?? "—"}</span>{hint && <span className="hint">{hint}</span>}</Tag>;
}
export function Card({ title, sub, action, children, className = "" }) {
  return <section className={`fx-card ${className}`}>{(title || action) && <div className="fx-card-head"><div>{title && <h3>{title}</h3>}{sub && <p className="fx-sub" style={{ margin: 0 }}>{sub}</p>}</div>{action}</div>}{children}</section>;
}
export const Empty = ({ children = "Nothing here yet.", icon: Icon = Inbox, title, action }) => <div className="fx-empty"><span className="fx-empty-icon" aria-hidden="true"><Icon size={22} /></span>{title && <strong className="fx-empty-title">{title}</strong>}<div>{children}</div>{action && <div className="fx-empty-action">{action}</div>}</div>;
export function Tabs({ tabs, value, onChange }) {
  return <div className="fx-tabs" role="tablist">{tabs.map((t) => <button key={t.key} role="tab" aria-selected={value === t.key} className={`fx-tab ${value === t.key ? "on" : ""}`} onClick={() => onChange(t.key)} data-testid={`tab-${t.key}`}>{t.icon && <t.icon size={15} />}{t.label}{t.count !== undefined && <span className="count">{t.count}</span>}</button>)}</div>;
}
export function Chips({ options, value, onChange }) {
  return <div className="fx-chips">{options.map((o) => <button key={o.key} className={`fx-chip ${value === o.key ? "on" : ""}`} onClick={() => onChange(o.key)} data-testid={`chip-${o.key}`}>{o.label}{o.count !== undefined && <b>{o.count}</b>}</button>)}</div>;
}
export function Modal({ title, eyebrow, onClose, children, wide, testId }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
    <div className={`review-modal ${wide ? "" : "add-user-modal"}`} role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
      <button className="modal-close" onClick={onClose} aria-label="Close"><X /></button>
      {eyebrow && <div className="modal-eyebrow">{eyebrow}</div>}
      {title && <h2>{title}</h2>}
      {children}
    </div>
  </div>;
}
export const Kv = ({ rows }) => <dl className="fx-kv">{rows.filter(Boolean).map(([k, v]) => <FragmentKv key={k} k={k} v={v} />)}</dl>;
const FragmentKv = ({ k, v }) => <><dt>{k}</dt><dd>{v === null || v === undefined || v === "" ? "—" : v}</dd></>;

// ---------------------------------------------------------------- data
export function useLoad(loader, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const alive = useRef(true);
  const run = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return loader().then((data) => { if (alive.current) setState({ data, loading: false, error: null }); return data; })
      .catch((error) => { if (alive.current) setState((s) => ({ ...s, loading: false, error })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { alive.current = true; run(); return () => { alive.current = false; }; }, [run]);
  return { ...state, reload: run };
}

let employeeCache = null;
export const loadEmployees = (force) => {
  if (!employeeCache || force) employeeCache = call("/admin/employees").then((rows) => rows.filter((r) => r.status === "active")).catch((e) => { employeeCache = null; throw e; });
  return employeeCache;
};
export function EmployeePicker({ value, onChange, placeholder = "Select employee", testId }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { loadEmployees().then(setRows).catch(() => setRows([])); }, []);
  const term = q.trim().toLowerCase();
  const shown = rows.filter((r) => !term || `${r.employee_name} ${r.employee_id} ${r.email}`.toLowerCase().includes(term)).slice(0, 200);
  return <div style={{ display: "grid", gap: 6 }}>
    <input className="fx-input" placeholder="Search name or ID…" value={q} onChange={(e) => setQ(e.target.value)} />
    <select className="fx-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{placeholder}</option>
      {shown.map((r) => <option key={r.id} value={r.employee_id}>{r.employee_name} ({r.employee_id}){r.role !== "employee" ? ` · ${humanize(r.role)}` : ""}</option>)}
    </select>
  </div>;
}

// ---------------------------------------------------------------- files
export function FilePicker({ files, onChange, max = 3, accept = "application/pdf,image/png,image/jpeg,image/webp", label = "Attach files" }) {
  return <div className="fx-drop">
    <Paperclip size={15} />
    <span>{label} (PDF/PNG/JPG/WEBP, ≤10 MB, up to {max})</span>
    <input type="file" multiple accept={accept} onChange={(e) => { onChange([...files, ...Array.from(e.target.files || [])].slice(0, max)); e.target.value = ""; }} />
    {files.map((f, i) => <span key={`${f.name}-${i}`} className="fx-file">{f.name}<button type="button" className="fx-link" onClick={() => onChange(files.filter((_, n) => n !== i))} aria-label={`Remove ${f.name}`}>×</button></span>)}
  </div>;
}
export const fileUrl = (path) => `${API}${path}`;
export const Attachment = ({ href, name }) => <a className="fx-file" href={href} target="_blank" rel="noreferrer"><FileText size={13} />{name || "Attachment"}</a>;
